import { CloudUpload } from "lucide-react";
import { observer } from "mobx-react-lite";
import { type FC, useEffect, useRef, useState } from "react";

import { Dialog } from "@/components/ui/dialog";
import { Dropzone, DropzoneContent } from "@/components/ui/shadcn-io/dropzone";
import { cn } from "@/lib/utils";
import { sanitizeSvgFile } from "@/utils/sanitizeSvg";

import ImageEditor from "./ImageEditor";
import ImageInProcess from "./ImageInProcess";
import type { MediaStore } from "./mediaStore";
import { MediaUtils } from "./mediaUtils";

const ACCEPTED_TYPES = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/svg+xml": [".svg"],
  "video/mp4": [".mp4"],
};

const DEFAULT_MAX_SIZE = 100 * 1024 * 1024;
const DEFAULT_MIN_SIZE = 1024;

type AcceptedTypes = Record<string, string[]>;

type FileSizeLimits = {
  min?: number;
  max?: number;
};

interface MediaUploaderProps {
  disabled: boolean;
  mediaStore: MediaStore;
  onError: (a: string) => void;
  acceptedTypes?: AcceptedTypes;
  sizeLimits?: FileSizeLimits;
  classes?: string;
  aspect?: number;
}

const MediaUploader: FC<MediaUploaderProps> = observer(
  ({
    disabled,
    mediaStore,
    onError,
    acceptedTypes = ACCEPTED_TYPES,
    sizeLimits = { min: DEFAULT_MIN_SIZE, max: DEFAULT_MAX_SIZE },
    classes,
    aspect,
  }) => {
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [uploading, setUploading] = useState<boolean>(false);

    const [editorOpen, setEditorOpen] = useState<boolean>(false);
    const [_uploadedFile, setUploadedFile] = useState<File | null>(null);

    const [showLabel, setShowLabel] = useState(true);
    const dropzoneRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (dropzoneRef.current) {
        const { offsetWidth, offsetHeight } = dropzoneRef.current;
        if (offsetWidth < 70 || offsetHeight < 70) {
          setShowLabel(false);
        } else {
          setShowLabel(true);
        }
      }
    }, [classes]);

    const simulateUpload = async (duration: number = 500) => {
      for (let progress = 0; progress < 100; progress += 10) {
        setUploadProgress(progress);
        await new Promise(resolve => setTimeout(resolve, duration / 10));
      }
      setUploadProgress(100);
    };

    const handleDrop = async (files: File[]) => {
      onError("");
      setUploading(true);
      setUploadProgress(0);

      const dropped = files[0];
      if (!dropped) {
        setUploading(false);
        return;
      }

      // Check if file type is accepted
      const isAccepted = Object.keys(acceptedTypes).includes(dropped.type);
      if (!isAccepted) {
        onError("Unsupported file format.");
        setUploading(false);
        return;
      }

      let file = dropped;
      if (dropped.type === "image/svg+xml") {
        try {
          file = await sanitizeSvgFile(dropped);
        } catch {
          onError("This SVG contains unsafe content.");
          setUploading(false);
          return;
        }
      }

      // Collect metadata
      const metadata = await MediaUtils.collectMetadata(file);

      // Ask store if we can add this media
      try {
        mediaStore.canAddMedia(metadata);
      } catch (error) {
        onError(error instanceof Error ? error.message : "Unknown error");
        setUploading(false);
        return;
      }

      await simulateUpload();

      if (metadata.type === "image") {
        if (file.type === "image/gif") {
          mediaStore.addMediaFile(file);
        } else {
          setUploadedFile(file);
          setEditorOpen(true);
        }
      } else {
        mediaStore.addMediaFile(file);
      }
      setUploading(false);
    };

    const handleImageSave = async (croppedImageBase64: string) => {
      if (!_uploadedFile) return;

      const blob = await (await fetch(croppedImageBase64)).blob();

      const editedFile = new File([blob], _uploadedFile.name, {
        type: blob.type,
      });

      // Add processed media to store
      mediaStore.addMediaFile(editedFile);

      setUploadedFile(null);
      setEditorOpen(false);
    };

    return (
      <div>
        <div
          className="relative w-fit"
          data-testid="file-upload-dropzone-container"
          ref={dropzoneRef}
        >
          {uploading && (
            <ImageInProcess value={uploadProgress} classes={classes} />
          )}
          {!uploading && !disabled && (
            <Dropzone
              maxSize={sizeLimits.max}
              minSize={sizeLimits.min}
              accept={acceptedTypes}
              onDrop={handleDrop}
              onError={err => {
                onError(err.message);
              }}
              className={cn(
                "border border-blue-400 border-dashed bg-transparent w-[106px] h-[106px] hover:bg-transparent hover:cursor-pointer",
                classes
              )}
            >
              <div className="absolute w-full h-full flex bg-blue-50 flex-col justify-center gap-2 items-center">
                <CloudUpload style={{ width: "32px", height: "32px" }} />
                {showLabel && (
                  <span className="text-xs font-bold">Upload a file</span>
                )}
              </div>
              <DropzoneContent />
            </Dropzone>
          )}
        </div>

        <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
          {_uploadedFile && (
            <ImageEditor
              onSave={handleImageSave}
              file={_uploadedFile}
              aspect={aspect}
            />
          )}
        </Dialog>
      </div>
    );
  }
);

export default MediaUploader;
